import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { InternalLayout } from "@/components/InternalLayout";
import { StatusPill } from "@/components/StatusPill";
import AuditEnginePanel from "@/components/AuditEnginePanel";
import { fmtDate, RUN_STATUS, STATUS_LABELS } from "@/lib/constants";
import { Plus, ShieldCheck, Lock, X, Check, AlertTriangle, FileSignature, Trash2 } from "lucide-react";
import { toast } from "sonner";

const FROZEN = (s) => s === "abgeschlossen" || s === "abgelehnt";

const DECISION_OPTIONS = [
  { value: "bestanden", label: "Bestanden", desc: "Lieferkette und Beweise tragen die Freigabe ohne Vorbehalt." },
  { value: "mit_auflagen", label: "Mit Auflagen", desc: "Freigabe möglich; Auflagen müssen für Sign-off erfüllt sein." },
  { value: "nicht_bestanden", label: "Nicht bestanden", desc: "Sign-off ausgeschlossen. Begründung Pflicht." },
];

const decisionPill = (decision) => {
  if (!decision) return <span className="font-mono text-[10px] text-neutral-400">— offen —</span>;
  const map = {
    bestanden: { bg: "#DCFCE7", text: "#166534", border: "#86EFAC" },
    mit_auflagen: { bg: "#FEF3C7", text: "#92400E", border: "#FDE68A" },
    nicht_bestanden: { bg: "#FEE2E2", text: "#7F1D1D", border: "#FCA5A5" },
  };
  const s = map[decision];
  const label = DECISION_OPTIONS.find((d) => d.value === decision)?.label || decision;
  return (
    <span
      data-testid={`audit-decision-${decision}`}
      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium border rounded-full"
      style={{ background: s.bg, color: s.text, borderColor: s.border }}
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: s.text }} />
      {label}
    </span>
  );
};

const checklistStatusStyle = (st) => {
  switch (st) {
    case "erfuellt": return { bg: "#DCFCE7", text: "#166534", border: "#86EFAC", label: "Erfüllt" };
    case "nicht_erfuellt": return { bg: "#FEE2E2", text: "#991B1B", border: "#FCA5A5", label: "Nicht erfüllt" };
    case "nicht_anwendbar": return { bg: "#F4F4F5", text: "#525252", border: "#D4D4D8", label: "Nicht anwendbar" };
    default: return { bg: "#FFF", text: "#525252", border: "#D4D4D8", label: "Offen" };
  }
};

export default function Audits() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [audits, setAudits] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [users, setUsers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showSignOff, setShowSignOff] = useState(null);
  const [signOffStatement, setSignOffStatement] = useState("");
  const [openId, setOpenId] = useState(null);
  const [form, setForm] = useState({ type: "acceptance", name: "", scope: "", auditor_id: "", referenced_review_ids: [], notes: "" });

  useEffect(() => {
    api.get("/projects").then((r) => {
      setProjects(r.data);
      const url = new URL(window.location.href);
      setProjectId(url.searchParams.get("project") || r.data[0]?.project_id || "");
    });
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  const load = async (id) => {
    if (!id) return;
    const [a, r] = await Promise.all([
      api.get(`/projects/${id}/audits`),
      api.get(`/projects/${id}/reviews`),
    ]);
    setAudits(a.data);
    setReviews(r.data);
  };
  useEffect(() => { load(projectId); }, [projectId]);

  const userName = (uid) => users.find((u) => u.user_id === uid)?.name || users.find((u) => u.user_id === uid)?.email || uid || "—";

  // Auditor-Pool: intern_audit oder admin
  const auditorCandidates = useMemo(
    () => users.filter((u) => (u.roles || []).some((r) => r === "intern_audit" || r === "admin")),
    [users]
  );

  // Acceptance-Audit benötigt abgeschlossene Reviews mit Ergebnis bestanden/mit_auflagen
  const eligibleReviews = useMemo(
    () => reviews.filter((r) => r.status === "abgeschlossen" && (r.result === "bestanden" || r.result === "mit_auflagen")),
    [reviews]
  );

  const onCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/projects/${projectId}/audits`, {
        type: form.type,
        name: form.name,
        scope: form.scope,
        auditor_id: form.auditor_id,
        referenced_review_ids: form.referenced_review_ids,
        notes: form.notes,
      });
      toast.success("Audit-Lauf angelegt");
      setShowNew(false);
      setForm({ type: "acceptance", name: "", scope: "", auditor_id: "", referenced_review_ids: [], notes: "" });
      load(projectId);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Anlegen fehlgeschlagen");
    }
  };

  const patch = async (audit_id, body) => {
    try {
      await api.patch(`/projects/${projectId}/audits/${audit_id}`, body);
      load(projectId);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Update fehlgeschlagen");
    }
  };

  const setStatus = async (audit_id, status) => {
    try {
      await api.patch(`/projects/${projectId}/audits/${audit_id}`, { status });
      toast.success(`Status: ${STATUS_LABELS[status]}`);
      load(projectId);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Statuswechsel fehlgeschlagen");
    }
  };

  const updateChecklistItem = (audit, idx, patchFields) => {
    const cl = audit.checklist.map((c, i) => (i === idx ? { ...c, ...patchFields } : c));
    patch(audit.audit_id, { checklist: cl });
  };

  const addAuflage = (audit) => {
    const beschreibung = window.prompt("Auflage beschreiben:");
    if (!beschreibung) return;
    const list = [...(audit.auflagen || []), { beschreibung, status: "offen" }];
    patch(audit.audit_id, { auflagen: list });
  };

  const updateAuflage = (audit, idx, patchFields) => {
    const list = audit.auflagen.map((a, i) => (i === idx ? { ...a, ...patchFields } : a));
    patch(audit.audit_id, { auflagen: list });
  };

  const removeAuflage = (audit, idx) => {
    const list = audit.auflagen.filter((_, i) => i !== idx);
    patch(audit.audit_id, { auflagen: list });
  };

  const submitSignOff = async () => {
    if (!showSignOff || !signOffStatement.trim()) return;
    try {
      await api.post(`/projects/${projectId}/audits/${showSignOff}/sign-off`, { statement: signOffStatement });
      toast.success("Sign-off dokumentiert");
      setShowSignOff(null);
      setSignOffStatement("");
      load(projectId);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Sign-off fehlgeschlagen");
    }
  };

  const open = audits.find((a) => a.audit_id === openId);
  const frozenOpen = open && FROZEN(open.status);
  const isSigned = open && open.sign_off && open.sign_off.signed_off_at;

  return (
    <InternalLayout>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Phase 5</div>
          <h1 className="font-display text-3xl md:text-4xl text-neutral-900 flex items-center gap-3">
            Audit <ShieldCheck className="w-6 h-6 text-[#183153]" />
          </h1>
          <p className="text-sm text-neutral-600 mt-2 max-w-2xl">
            Beweisaufnahme über die Lieferkette. Audit prüft das Wie, nicht das Was. Sign-off ist einmalig und unwiderruflich. Audits leben im Portal — kein automatisches Spiegeln nach <code>project.yaml</code>.
          </p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          disabled={!projectId}
          data-testid="audits-new-button"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white rounded-sm disabled:opacity-50"
          style={{ background: "#183153" }}
        >
          <Plus className="w-4 h-4" /> Neuer Audit
        </button>
      </div>

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <select
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          data-testid="audits-project-select"
          className="px-3 py-2 border border-neutral-300 rounded-sm text-sm"
        >
          <option value="">Projekt wählen…</option>
          {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
        </select>
      </div>

      {projectId && <AuditEnginePanel projectId={projectId} />}

      {projectId && (
        <div className="space-y-3">
          {audits.length === 0 && (
            <div className="bg-white border border-neutral-200 px-5 py-10 text-center text-sm text-neutral-500" data-testid="audits-empty">
              Noch keine Audits.
            </div>
          )}
          {audits.map((a) => {
            const frozen = FROZEN(a.status);
            const signed = a.sign_off && a.sign_off.signed_off_at;
            return (
              <div key={a.audit_id} className="bg-white border border-neutral-200" data-testid={`audit-${a.audit_id}`}>
                <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between flex-wrap gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-neutral-900 flex items-center gap-2">
                      {frozen && <Lock className="w-3.5 h-3.5 text-neutral-500" />}
                      {signed && <FileSignature className="w-3.5 h-3.5 text-emerald-700" />}
                      {a.name || a.audit_id}
                      <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 px-1.5 py-0.5 border border-neutral-200">
                        {a.type === "acceptance" ? "ACCEPTANCE" : "HEALTH"}
                      </span>
                    </div>
                    <div className="font-mono text-[10px] text-neutral-500">
                      {a.audit_id} · erstellt {fmtDate(a.created_at)} · Auditor: {a.auditor_name || userName(a.auditor_id)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {decisionPill(a.decision)}
                    <StatusPill kind="status" value={a.status} />
                    <button
                      onClick={() => setOpenId(a.audit_id)}
                      data-testid={`audit-open-${a.audit_id}`}
                      className="text-xs px-2 py-1 border border-neutral-300 rounded-sm hover:bg-neutral-50"
                    >
                      Details
                    </button>
                  </div>
                </div>
                {signed && (
                  <div className="px-5 py-2 bg-emerald-50 border-b border-emerald-100 text-xs text-emerald-900 flex items-center gap-2">
                    <FileSignature className="w-3.5 h-3.5" />
                    Signiert am {fmtDate(a.sign_off.signed_off_at)} durch {a.sign_off.signed_off_by_name}
                  </div>
                )}
                <div className="px-5 py-3 grid md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Scope</div>
                    <div className="text-neutral-700 whitespace-pre-wrap text-xs">{a.scope || "—"}</div>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Bezogene Reviews</div>
                    <div className="text-neutral-700 text-xs">
                      {(a.referenced_review_ids || []).length === 0 ? "—" : (a.referenced_review_ids || []).map((rid) => {
                        const r = reviews.find((x) => x.review_id === rid);
                        return <div key={rid} className="font-mono">{rid} {r?.result ? `· ${r.result}` : ""}</div>;
                      })}
                    </div>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Checkliste</div>
                    <div className="text-xs text-neutral-700">
                      {(a.checklist || []).filter((c) => c.status === "erfuellt").length}/{(a.checklist || []).length} erfüllt
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Anlegen-Modal */}
      {showNew && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <div className="bg-white border border-neutral-200 max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-neutral-200 font-display-medium flex items-center justify-between sticky top-0 bg-white">
              <span>Neuer Audit-Lauf</span>
              <button onClick={() => setShowNew(false)} className="text-neutral-400 hover:text-neutral-700"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={onCreate} className="p-5 space-y-3">
              <div>
                <label className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">Audit-Typ <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {[
                    { v: "acceptance", t: "Acceptance", d: "Vor Sign-off zur Freigabe an den Kunden" },
                    { v: "health", t: "Health", d: "Wiederkehrende Betriebsprüfung; kein Sign-off" },
                  ].map((opt) => (
                    <label key={opt.v} className={`border px-3 py-2 cursor-pointer text-sm ${form.type === opt.v ? "border-[#183153] bg-blue-50" : "border-neutral-200 hover:bg-neutral-50"}`}>
                      <input type="radio" name="audit-type" value={opt.v} checked={form.type === opt.v} onChange={() => setForm({ ...form, type: opt.v, referenced_review_ids: [] })} data-testid={`audit-type-${opt.v}`} className="mr-2" />
                      <span className="font-medium">{opt.t}</span>
                      <div className="text-[11px] text-neutral-500 mt-0.5">{opt.d}</div>
                    </label>
                  ))}
                </div>
              </div>
              <input
                required
                placeholder="Audit-Name (z. B. Abnahme Lieferversion 1.0)"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="audit-name-input"
                className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm"
              />
              <textarea
                rows={2}
                placeholder="Scope (was wird geprüft?)"
                value={form.scope}
                onChange={(e) => setForm({ ...form, scope: e.target.value })}
                data-testid="audit-scope-input"
                className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm"
              />
              <div>
                <label className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">
                  Auditor <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={form.auditor_id}
                  onChange={(e) => setForm({ ...form, auditor_id: e.target.value })}
                  data-testid="audit-auditor-select"
                  className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm text-sm"
                >
                  <option value="">— wählen —</option>
                  {auditorCandidates.map((u) => (
                    <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>
                  ))}
                </select>
                <div className="text-[11px] text-neutral-500 mt-1">
                  Eignungsregel (hart): Auditor ≠ Reviewer der bezogenen Reviews ≠ Build-Owner.
                </div>
              </div>
              {form.type === "acceptance" && (
                <div>
                  <label className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">Bezogene Reviews</label>
                  <div className="mt-1 border border-neutral-200 max-h-40 overflow-y-auto">
                    {eligibleReviews.length === 0 && (
                      <div className="px-3 py-2 text-xs text-amber-700">
                        Keine Reviews mit Ergebnis bestanden / mit_auflagen verfügbar. Acceptance-Audit kann angelegt werden, kann aber erst abgeschlossen werden, wenn ein passendes Review existiert.
                      </div>
                    )}
                    {eligibleReviews.map((r) => (
                      <label key={r.review_id} className="flex items-start gap-2 px-3 py-1.5 border-b border-neutral-100 text-xs">
                        <input
                          type="checkbox"
                          checked={form.referenced_review_ids.includes(r.review_id)}
                          onChange={(e) => setForm({
                            ...form,
                            referenced_review_ids: e.target.checked
                              ? [...form.referenced_review_ids, r.review_id]
                              : form.referenced_review_ids.filter((x) => x !== r.review_id),
                          })}
                          data-testid={`audit-ref-review-${r.review_id}`}
                          className="mt-0.5"
                        />
                        <div>
                          <span className="font-mono">{r.review_id}</span> · {r.name} · Ergebnis: <strong>{r.result}</strong>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <textarea
                rows={2}
                placeholder="Notizen (optional)"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm"
              />
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowNew(false)} className="px-4 py-2 text-sm border border-neutral-300 rounded-sm hover:bg-neutral-50">Abbrechen</button>
                <button type="submit" data-testid="audit-submit-button" className="px-4 py-2 text-sm text-white rounded-sm" style={{ background: "#183153" }}>Anlegen</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail-Drawer */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={() => setOpenId(null)}>
          <div
            className="bg-white border-l border-neutral-200 w-full max-w-2xl h-full overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid={`audit-drawer-${open.audit_id}`}
          >
            <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{open.audit_id} · {open.type.toUpperCase()}</div>
                <div className="text-base font-medium text-neutral-900 flex items-center gap-2">
                  {frozenOpen && <Lock className="w-4 h-4 text-neutral-500" />} {open.name}
                </div>
              </div>
              <button onClick={() => setOpenId(null)} className="text-neutral-400 hover:text-neutral-700"><X className="w-4 h-4" /></button>
            </div>

            <div className="px-5 py-4 space-y-5">
              {isSigned && (
                <div className="px-3 py-2 bg-emerald-50 border border-emerald-200 text-xs text-emerald-900 flex items-start gap-2">
                  <FileSignature className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-medium">Signiert</div>
                    <div>Am {fmtDate(open.sign_off.signed_off_at)} durch {open.sign_off.signed_off_by_name}</div>
                    <div className="mt-1 italic">„{open.sign_off.sign_off_statement}"</div>
                  </div>
                </div>
              )}
              {frozenOpen && !isSigned && (
                <div className="px-3 py-2 bg-neutral-50 border border-neutral-200 text-xs text-neutral-700 flex items-start gap-2">
                  <Lock className="w-3.5 h-3.5 mt-0.5 text-neutral-500 flex-shrink-0" />
                  Dieses Audit ist eingefroren. Für eine Iteration lege einen neuen Audit-Run an.
                </div>
              )}

              {/* Stammdaten */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Auditor</div>
                  {frozenOpen ? (
                    <div className="text-neutral-700">{open.auditor_name || userName(open.auditor_id)}</div>
                  ) : (
                    <select
                      value={open.auditor_id || ""}
                      onChange={(e) => patch(open.audit_id, { auditor_id: e.target.value })}
                      data-testid="audit-auditor-edit"
                      className="w-full px-2 py-1.5 border border-neutral-300 rounded-sm text-sm"
                    >
                      <option value="">— wählen —</option>
                      {auditorCandidates.map((u) => (
                        <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Typ</div>
                  <div className="text-neutral-700 font-mono text-xs">{open.type}</div>
                </div>
              </div>

              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Scope</div>
                {frozenOpen ? (
                  <div className="text-sm text-neutral-700 whitespace-pre-wrap">{open.scope || "—"}</div>
                ) : (
                  <textarea
                    rows={2}
                    defaultValue={open.scope || ""}
                    onBlur={(e) => patch(open.audit_id, { scope: e.target.value })}
                    data-testid="audit-scope-edit"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm"
                  />
                )}
              </div>

              {open.type === "acceptance" && (
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Bezogene Reviews</div>
                  {frozenOpen ? (
                    <div className="text-xs text-neutral-700">
                      {(open.referenced_review_ids || []).length === 0 ? "—" : (open.referenced_review_ids || []).map((rid) => (
                        <div key={rid} className="font-mono">{rid}</div>
                      ))}
                    </div>
                  ) : (
                    <div className="border border-neutral-200 max-h-32 overflow-y-auto">
                      {reviews.filter((r) => r.status === "abgeschlossen").length === 0 && (
                        <div className="px-3 py-2 text-xs text-neutral-500">Keine abgeschlossenen Reviews.</div>
                      )}
                      {reviews.filter((r) => r.status === "abgeschlossen").map((r) => (
                        <label key={r.review_id} className="flex items-start gap-2 px-3 py-1.5 border-b border-neutral-100 text-xs">
                          <input
                            type="checkbox"
                            checked={(open.referenced_review_ids || []).includes(r.review_id)}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...(open.referenced_review_ids || []), r.review_id]
                                : (open.referenced_review_ids || []).filter((x) => x !== r.review_id);
                              patch(open.audit_id, { referenced_review_ids: next });
                            }}
                            data-testid={`audit-ref-toggle-${r.review_id}`}
                            className="mt-0.5"
                          />
                          <div>
                            <span className="font-mono">{r.review_id}</span> · {r.name} · {r.result || "—"}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Checkliste */}
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Checkliste</div>
                <div className="space-y-2">
                  {(open.checklist || []).map((c, i) => {
                    const s = checklistStatusStyle(c.status);
                    return (
                      <div key={c.id || i} className="border border-neutral-200" data-testid={`audit-check-${open.audit_id}-${i}`}>
                        <div className="px-3 py-2 flex items-center justify-between gap-2 flex-wrap">
                          <div className="text-sm flex-1 min-w-0">{c.title}</div>
                          {frozenOpen ? (
                            <span
                              className="inline-flex items-center px-2 py-0.5 text-[11px] border rounded-full"
                              style={{ background: s.bg, color: s.text, borderColor: s.border }}
                            >
                              {s.label}
                            </span>
                          ) : (
                            <select
                              value={c.status}
                              onChange={(e) => updateChecklistItem(open, i, { status: e.target.value })}
                              data-testid={`audit-check-status-${open.audit_id}-${i}`}
                              className="px-2 py-1 text-xs border border-neutral-300 rounded-sm"
                            >
                              <option value="offen">Offen</option>
                              <option value="erfuellt">Erfüllt</option>
                              <option value="nicht_erfuellt">Nicht erfüllt</option>
                              <option value="nicht_anwendbar">Nicht anwendbar</option>
                            </select>
                          )}
                        </div>
                        <div className="px-3 pb-2 grid md:grid-cols-2 gap-2 text-xs">
                          <div>
                            <div className="text-[10px] text-neutral-500 mb-0.5">Evidenz {c.status === "erfuellt" && !frozenOpen && <span className="text-red-500">*</span>}</div>
                            {frozenOpen ? (
                              <div className="text-neutral-700 whitespace-pre-wrap">{c.evidence || "—"}</div>
                            ) : (
                              <textarea
                                rows={1}
                                defaultValue={c.evidence || ""}
                                onBlur={(e) => updateChecklistItem(open, i, { evidence: e.target.value })}
                                data-testid={`audit-check-evidence-${open.audit_id}-${i}`}
                                className="w-full px-2 py-1 border border-neutral-200 rounded-sm"
                                placeholder="Beleg / Verweis"
                              />
                            )}
                          </div>
                          <div>
                            <div className="text-[10px] text-neutral-500 mb-0.5">
                              Begründung {(c.status === "nicht_erfuellt" || c.status === "nicht_anwendbar") && !frozenOpen && <span className="text-red-500">*</span>}
                            </div>
                            {frozenOpen ? (
                              <div className="text-neutral-700 whitespace-pre-wrap">{c.note || "—"}</div>
                            ) : (
                              <textarea
                                rows={1}
                                defaultValue={c.note || ""}
                                onBlur={(e) => updateChecklistItem(open, i, { note: e.target.value })}
                                data-testid={`audit-check-note-${open.audit_id}-${i}`}
                                className="w-full px-2 py-1 border border-neutral-200 rounded-sm"
                                placeholder="Begründung"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Decision */}
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Decision</div>
                {frozenOpen ? (
                  <div>{decisionPill(open.decision)}</div>
                ) : (
                  <div className="grid grid-cols-1 gap-1.5">
                    {DECISION_OPTIONS.map((d) => (
                      <label
                        key={d.value}
                        className={`flex items-start gap-2 border px-3 py-2 cursor-pointer text-sm ${
                          open.decision === d.value ? "border-[#183153] bg-blue-50" : "border-neutral-200 hover:bg-neutral-50"
                        }`}
                      >
                        <input
                          type="radio"
                          name="audit-decision"
                          value={d.value}
                          checked={open.decision === d.value}
                          onChange={() => patch(open.audit_id, { decision: d.value })}
                          data-testid={`audit-decision-radio-${d.value}`}
                          className="mt-0.5"
                        />
                        <div>
                          <div className="font-medium text-neutral-900">{d.label}</div>
                          <div className="text-[11px] text-neutral-500">{d.desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Decision-Note */}
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                  Begründung der Decision {!frozenOpen && <span className="text-red-500">*</span>}
                </div>
                {frozenOpen ? (
                  <div className="text-sm text-neutral-700 whitespace-pre-wrap">{open.decision_note || "—"}</div>
                ) : (
                  <textarea
                    rows={3}
                    defaultValue={open.decision_note || ""}
                    onBlur={(e) => patch(open.audit_id, { decision_note: e.target.value })}
                    data-testid="audit-decision-note-edit"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm"
                  />
                )}
              </div>

              {/* Auflagen */}
              {(open.decision === "mit_auflagen" || (open.auflagen || []).length > 0) && (
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2 flex items-center justify-between">
                    <span>Auflagen {open.decision === "mit_auflagen" && !frozenOpen && <span className="text-red-500">*</span>}</span>
                    {!frozenOpen && (
                      <button onClick={() => addAuflage(open)} data-testid="audit-add-auflage" className="text-xs px-2 py-0.5 border border-neutral-300 rounded-sm hover:bg-neutral-50">+ Auflage</button>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    {(open.auflagen || []).length === 0 && <div className="text-xs text-neutral-500">— keine —</div>}
                    {(open.auflagen || []).map((a, i) => (
                      <div key={a.id || i} className="flex items-start gap-2 border border-neutral-200 px-3 py-2 text-sm" data-testid={`audit-auflage-${i}`}>
                        <input
                          type="checkbox"
                          checked={a.status === "erfuellt"}
                          disabled={frozenOpen}
                          onChange={(e) => updateAuflage(open, i, { status: e.target.checked ? "erfuellt" : "offen" })}
                          data-testid={`audit-auflage-toggle-${i}`}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0 text-xs">
                          {frozenOpen ? (
                            <div className={a.status === "erfuellt" ? "text-neutral-500 line-through" : ""}>{a.beschreibung}</div>
                          ) : (
                            <input
                              defaultValue={a.beschreibung}
                              onBlur={(e) => updateAuflage(open, i, { beschreibung: e.target.value })}
                              data-testid={`audit-auflage-text-${i}`}
                              className={`w-full px-2 py-1 border border-neutral-200 rounded-sm ${a.status === "erfuellt" ? "text-neutral-500 line-through" : ""}`}
                            />
                          )}
                        </div>
                        {!frozenOpen && (
                          <button onClick={() => removeAuflage(open, i)} className="text-neutral-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Nicht-bestanden-Grund */}
              {open.decision === "nicht_bestanden" && (
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                    Grund (nicht_bestanden) {!frozenOpen && <span className="text-red-500">*</span>}
                  </div>
                  {frozenOpen ? (
                    <div className="text-sm text-neutral-700 whitespace-pre-wrap">{open.nicht_bestanden_grund || "—"}</div>
                  ) : (
                    <textarea
                      rows={2}
                      defaultValue={open.nicht_bestanden_grund || ""}
                      onBlur={(e) => patch(open.audit_id, { nicht_bestanden_grund: e.target.value })}
                      data-testid="audit-nbg-edit"
                      className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm"
                    />
                  )}
                </div>
              )}

              {/* Notizen */}
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Notizen</div>
                {frozenOpen ? (
                  <div className="text-sm text-neutral-700 whitespace-pre-wrap">{open.notes || "—"}</div>
                ) : (
                  <textarea
                    rows={2}
                    defaultValue={open.notes || ""}
                    onBlur={(e) => patch(open.audit_id, { notes: e.target.value })}
                    data-testid="audit-notes-edit"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm"
                  />
                )}
              </div>

              {/* Status & Sign-off */}
              <div className="border-t border-neutral-200 pt-4 space-y-3">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Status</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusPill kind="status" value={open.status} />
                    {!frozenOpen && (
                      <select
                        value={open.status}
                        onChange={(e) => setStatus(open.audit_id, e.target.value)}
                        data-testid={`audit-status-${open.audit_id}`}
                        className="px-2 py-1 text-xs border border-neutral-300 rounded-sm"
                      >
                        {RUN_STATUS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                      </select>
                    )}
                  </div>
                  {!frozenOpen && (
                    <div className="text-[11px] text-neutral-500 mt-2 leading-relaxed">
                      Für <strong>abgeschlossen</strong>: kein offener Punkt, Decision gewählt + begründet, Auflagen bei <em>mit_auflagen</em> nicht leer, Begründung bei <em>nicht_bestanden</em> gesetzt. Bei Acceptance zusätzlich: mind. ein referenziertes, abgeschlossenes Review mit Ergebnis bestanden / mit_auflagen.
                    </div>
                  )}
                </div>

                {open.type === "acceptance" && open.status === "abgeschlossen" && !isSigned && (open.decision === "bestanden" || open.decision === "mit_auflagen") && (
                  <div className="border border-emerald-200 bg-emerald-50 px-3 py-3 rounded-sm">
                    <div className="font-medium text-emerald-900 text-sm flex items-center gap-2">
                      <FileSignature className="w-4 h-4" /> Sign-off bereit
                    </div>
                    <div className="text-xs text-emerald-900 mt-1">
                      Sign-off ist einmalig und unwiderruflich. Phase wechselt anschließend auf <code>sign_off</code> und erscheint im Kundenportal als Vertrauensanker.
                    </div>
                    {open.decision === "mit_auflagen" && (open.auflagen || []).some((a) => a.status !== "erfuellt") && (
                      <div className="text-xs text-amber-800 mt-2 flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5" /> Sign-off blockiert: noch offene Auflagen.
                      </div>
                    )}
                    <button
                      onClick={() => { setShowSignOff(open.audit_id); setSignOffStatement(""); }}
                      data-testid={`audit-signoff-${open.audit_id}`}
                      className="mt-2 inline-flex items-center gap-2 px-4 py-2 text-sm text-white rounded-sm"
                      style={{ background: "#166534" }}
                    >
                      <Check className="w-4 h-4" /> Sign-off durchführen
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sign-off Modal */}
      {showSignOff && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowSignOff(null)}>
          <div className="bg-white border border-neutral-200 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-neutral-200 font-display-medium flex items-center gap-2">
              <FileSignature className="w-4 h-4 text-emerald-700" /> Sign-off durchführen
            </div>
            <div className="p-5 space-y-3">
              <div className="text-xs text-neutral-700 bg-amber-50 border border-amber-200 px-3 py-2">
                <strong>Achtung:</strong> Sign-off ist <strong>unwiderruflich</strong>. Du bestätigst die formale Abnahmefähigkeit der Lieferkette.
              </div>
              <textarea
                required
                rows={4}
                placeholder="Sign-off-Erklärung (was wird abgenommen, auf welcher Basis?)"
                value={signOffStatement}
                onChange={(e) => setSignOffStatement(e.target.value)}
                data-testid="audit-signoff-statement"
                className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm"
              />
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowSignOff(null)} className="px-4 py-2 text-sm border border-neutral-300 rounded-sm hover:bg-neutral-50">Abbrechen</button>
                <button
                  onClick={submitSignOff}
                  disabled={!signOffStatement.trim()}
                  data-testid="audit-signoff-confirm"
                  className="px-4 py-2 text-sm text-white rounded-sm disabled:opacity-50"
                  style={{ background: "#166534" }}
                >
                  Sign-off bestätigen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </InternalLayout>
  );
}
